import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { routePaths } from '../app/route-paths.js';
import styles from '../components/layout/AuthLayout.module.css';
import { Button } from '../components/ui/Button.js';
import { FormError } from '../components/ui/FormError.js';
import fieldStyles from '../components/ui/fields.module.css';
import { TextField } from '../components/ui/TextField.js';
import { authClient } from '../lib/auth-client.js';
import { authErrorMessage } from '../lib/auth-errors.js';

const schema = z.object({ email: z.email('Enter a valid email address') });

type Values = z.infer<typeof schema>;

export function ForgotPasswordPage(): ReactElement {
    const [serverError, setServerError] = useState<string | null>(null);
    const [sentTo, setSentTo] = useState<string | null>(null);
    const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

    async function onSubmit(values: Values): Promise<void> {
        setServerError(null);
        const result = await authClient.requestPasswordReset({
            email: values.email,
            redirectTo: routePaths.resetPassword,
        });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not send the email'));
            return;
        }
        setSentTo(values.email);
    }

    if (sentTo !== null) {
        return (
            <>
                <h1>Check your email</h1>
                <p>
                    If an account exists for <strong>{sentTo}</strong>, a link to choose a new
                    password is on its way. The link stays valid for one hour.
                </p>
                <p className={styles.links}>
                    <Link to={routePaths.signIn}>Back to sign in</Link>
                </p>
            </>
        );
    }

    return (
        <>
            <h1>Forgot password</h1>
            <p>
                Enter the email for your account and we will send a link to choose a new password.
            </p>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    void form.handleSubmit(onSubmit)(event);
                }}
            >
                <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    error={form.formState.errors.email?.message}
                    {...form.register('email')}
                />
                <FormError message={serverError} />
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Send reset link
                </Button>
            </form>
            <p className={styles.links}>
                <Link to={routePaths.signIn}>Back to sign in</Link>
            </p>
        </>
    );
}
